import React, { useCallback, useEffect, useState } from 'react';
import { expensesAPI, usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/dateUtils';
import { parseApiError } from '../utils/errors';

const STATUS_COLOR = { pending: 'orange', approved: 'blue', rejected: 'red', reimbursed: 'green' };
const CATEGORIES = ['Travel', 'Food', 'Stationery', 'Internet', 'Client Visit', 'Other'];

export default function Expenses() {
  const { user } = useAuth();
  const isManager = user?.role === 'hr' || user?.role === 'super_admin';

  const now = new Date();
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    expense_date: new Date().toISOString().slice(0, 10),
    amount: '', category: 'Travel', description: '',
  });
  const [receipt, setReceipt] = useState(null);

  const [fStatus, setFStatus] = useState('');
  const [fUser, setFUser] = useState('');
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));

  const load = useCallback(() => {
    const params = {};
    if (fStatus) params.expense_status = fStatus;
    if (isManager && fUser) params.user_id = parseInt(fUser);
    if (month && year) { params.month = parseInt(month); params.year = parseInt(year); }
    setLoading(true);
    expensesAPI.list(params)
      .then(({ data }) => setItems(data))
      .catch((e) => setError(parseApiError(e, 'Failed to load expenses')))
      .finally(() => setLoading(false));
  }, [fStatus, fUser, month, year, isManager]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isManager) usersAPI.list({ is_active: true }).then(({ data }) => setPeople(data)).catch(() => {});
  }, [isManager]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.amount || parseFloat(form.amount) <= 0) { setError('Enter a valid amount'); return; }
    setSaving(true);
    try {
      const { data } = await expensesAPI.create({
        expense_date: form.expense_date,
        amount: parseFloat(form.amount),
        category: form.category,
        description: form.description || null,
      });
      if (receipt) {
        const fd = new FormData();
        fd.append('file', receipt);
        await expensesAPI.uploadReceipt(data.id, fd);
      }
      setShowForm(false);
      setForm({ expense_date: new Date().toISOString().slice(0, 10), amount: '', category: 'Travel', description: '' });
      setReceipt(null);
      flash('Expense submitted');
      load();
    } catch (err) {
      setError(parseApiError(err, 'Failed to submit expense'));
    } finally { setSaving(false); }
  };

  const review = async (item, status) => {
    try {
      await expensesAPI.review(item.id, { status });
      flash(`Marked ${status}`);
      load();
    } catch (err) { setError(parseApiError(err, 'Update failed')); }
  };

  const reimburseAll = async () => {
    if (!window.confirm(`Mark all pending/approved expenses for ${month}/${year} as reimbursed?`)) return;
    try {
      const params = { month: parseInt(month), year: parseInt(year) };
      if (fUser) params.user_id = parseInt(fUser);
      const { data } = await expensesAPI.reimburseMonth(params);
      flash(`${data.length} expense(s) marked reimbursed`);
      load();
    } catch (err) { setError(parseApiError(err, 'Bulk reimbursement failed')); }
  };

  const removeItem = async (item) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await expensesAPI.remove(item.id); load(); }
    catch (err) { setError(parseApiError(err, 'Delete failed')); }
  };

  const openReceipt = async (item) => {
    try {
      const res = await expensesAPI.downloadReceipt(item.id);
      // Preserve the real content type so the browser renders the PDF/image
      // instead of showing raw bytes as text.
      const type = res.headers?.['content-type'] || res.data?.type || 'application/octet-stream';
      const blob = new Blob([res.data], { type });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { setError('Could not open receipt'); }
  };

  const total = items.reduce((a, i) => a + (i.amount || 0), 0);
  const outstanding = items.filter((i) => i.status !== 'reimbursed').reduce((a, i) => a + (i.amount || 0), 0);

  return (
    <div className="page">
      <div className="page-header">
        <h2>Expenses</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add Expense'}
        </button>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {showForm && (
        <form className="form-card mb-4" onSubmit={submit}>
          <div className="form-row">
            <div className="form-group">
              <label>Date *</label>
              <input type="date" required value={form.expense_date}
                onChange={(e) => setForm({ ...form, expense_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Amount *</label>
              <input type="number" step="0.01" min="0" required value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="e.g. 250" />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea rows={2} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What was this expense for?" />
          </div>
          <div className="form-group">
            <label>Receipt (optional — image or PDF, max 10 MB)</label>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setReceipt(e.target.files?.[0] || null)} />
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Submit Expense'}</button>
          </div>
        </form>
      )}

      <div className="filter-bar">
        <select value={month} onChange={(e) => setMonth(e.target.value)}>
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</option>
          ))}
        </select>
        <input type="number" value={year} onChange={(e) => setYear(e.target.value)} style={{ width: 100 }} />
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="reimbursed">Reimbursed</option>
        </select>
        {isManager && (
          <select value={fUser} onChange={(e) => setFUser(e.target.value)}>
            <option value="">All Employees</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>
        )}
        {isManager && (
          <button className="btn btn-success" onClick={reimburseAll}>✅ Reimburse Month</button>
        )}
      </div>

      <div className="stats-grid mb-4">
        <div className="stat-card stat-card-blue">
          <div className="stat-icon">🧾</div>
          <div className="stat-body"><div className="stat-value">{items.length}</div><div className="stat-title">Expenses</div></div>
        </div>
        <div className="stat-card stat-card-purple">
          <div className="stat-icon">Σ</div>
          <div className="stat-body"><div className="stat-value">{total.toFixed(2)}</div><div className="stat-title">Total</div></div>
        </div>
        <div className="stat-card stat-card-orange">
          <div className="stat-icon">⏳</div>
          <div className="stat-body"><div className="stat-value">{outstanding.toFixed(2)}</div><div className="stat-title">Not Reimbursed</div></div>
        </div>
      </div>

      {loading ? <div className="page-loading">Loading…</div> : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>{isManager && <th>Employee</th>}<th>Category</th><th>Description</th>
                <th>Amount</th><th>Receipt</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={isManager ? 8 : 7} className="text-center text-muted">No expenses for this period</td></tr>
              ) : items.map((i) => (
                <tr key={i.id}>
                  <td>{fmtDate(i.expense_date, true)}</td>
                  {isManager && <td>{i.user?.full_name || '—'}</td>}
                  <td>{i.category || '—'}</td>
                  <td style={{ maxWidth: 260, overflowWrap: 'anywhere' }}>{i.description || '—'}</td>
                  <td><strong>{i.amount?.toFixed(2)}</strong></td>
                  <td>
                    {i.receipt_original_name
                      ? <button className="btn btn-sm btn-ghost" onClick={() => openReceipt(i)}>View</button>
                      : <span className="text-muted">—</span>}
                  </td>
                  <td><span className={`badge badge-${STATUS_COLOR[i.status]}`}>{i.status}</span></td>
                  <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {isManager && i.status !== 'reimbursed' && (
                      <>
                        {i.status !== 'approved' && (
                          <button className="btn btn-sm btn-ghost" onClick={() => review(i, 'approved')}>Approve</button>
                        )}
                        {i.status !== 'rejected' && (
                          <button className="btn btn-sm btn-ghost" onClick={() => review(i, 'rejected')}>Reject</button>
                        )}
                        <button className="btn btn-sm btn-success" onClick={() => review(i, 'reimbursed')}>Reimburse</button>
                      </>
                    )}
                    {(isManager || (i.user_id === user?.id && i.status !== 'reimbursed')) && (
                      <button className="btn btn-sm btn-danger" onClick={() => removeItem(i)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
